import React, { useState, useEffect, createContext, useContext } from "react";
import {
  motion,
  useScroll,
  AnimatePresence,
  useMotionValueEvent,
} from "framer-motion";
import { Menu, X } from "lucide-react";

// ============ NOTIFICATION SYSTEM ============
const NotificationContext = createContext();

const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showNotification = (message, type = "success", duration = 3000) => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);

    if (duration) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((notif) => notif.id !== id));
      }, duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  return (
    <NotificationContext.Provider
      value={{ showNotification, removeNotification }}
    >
      {children}
      <div
        className="fixed top-24 right-4 z-50 flex flex-col gap-3"
        style={{ pointerEvents: "none" }}
      >
        {notifications.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, y: -10, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -10, x: 20 }}
            className={`px-4 py-3 rounded-lg text-white text-sm font-medium pointer-events-auto ${
              notif.type === "success"
                ? "bg-green-500"
                : notif.type === "error"
                  ? "bg-red-500"
                  : "bg-blue-500"
            }`}
          >
            {notif.message}
          </motion.div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

// ============ DYNAMIC NAVBAR COMPONENT ============
const DynamicNavbar = ({ onOpenModal }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest < 100) {
      setIsVisible(true);
    } else if (latest > lastScrollY) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(latest);
  });

  return (
    <motion.nav
      className="fixed top-0 w-full z-50 border-b border-black/5"
      style={{ backdropFilter: "blur(25px)", backgroundColor: "rgba(255,255,255,0.8)" }}
      initial={{ y: 0 }}
      animate={{ y: isVisible ? 0 : -100 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      data-testid="dynamic-navbar"
    >
      <div className="px-4 sm:px-6 md:px-10 xl:px-16 flex justify-between items-center w-full h-[64px] sm:h-[72px] md:h-[80px]">
        <motion.a
          href="/"
          data-testid="navbar-logo"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/Logo.svg"
            alt="MyBovine.ai"
            className="h-[30px] sm:h-[36px] md:h-[44px] w-auto"
          />
        </motion.a>

        <div className="flex items-center gap-4">
          <motion.button
            onClick={onOpenModal}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="hidden sm:flex items-center justify-center text-[16px] font-medium bg-[#00BD7E] text-white px-[16px] py-[12px] rounded-[8px] hover:bg-[#00a86b] transition-colors"
            style={{ fontFamily: "Satoshi, sans-serif" }}
            data-testid="navbar-cta-btn"
          >
            Book a Farm Assessment
          </motion.button>

          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="sm:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="sm:hidden border-t border-black/5"
            style={{ backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(25px)" }}
          >
            <div className="px-4 py-4">
              <motion.button
                onClick={() => {
                  onOpenModal();
                  setIsMobileMenuOpen(false);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="text-[16px] font-medium bg-[#00BD7E] text-white px-[16px] py-[12px] rounded-[8px] hover:bg-[#00a86b] transition-colors w-full"
                style={{ fontFamily: "Satoshi, sans-serif" }}
              >
                Book a Farm Assessment
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// ============ FOOTER COMPONENT ============
const Footer = ({ onOpenModal }) => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isNewsletterLoading, setIsNewsletterLoading] = useState(false);
  const { showNotification } = useNotification();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      showNotification("Please enter a valid email address", "error", 3000);
      return;
    }

    setIsNewsletterLoading(true);

    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/newsletter",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: newsletterEmail,
          }),
        },
      );

      if (response.ok) {
        showNotification(
          "Successfully subscribed to newsletter!",
          "success",
          3000,
        );
        setNewsletterEmail("");
      } else {
        showNotification(
          "Failed to subscribe. Please try again.",
          "error",
          3000,
        );
      }
    } catch (error) {
      showNotification(
        "An error occurred. Please try again later.",
        "error",
        3000,
      );
    } finally {
      setIsNewsletterLoading(false);
    }
  };

  return (
    <footer className="w-full bg-[#0f141f] py-12 md:py-16" data-testid="footer">
      <div className="px-6 md:px-12 2xl:px-[64px] max-w-full mx-auto w-full">
        <div className="flex flex-col md:flex-row justify-between gap-12 md:gap-8 pb-8">
          {/* Col 1 - Company Logo & Description */}
          <div className="flex flex-col gap-3 max-w-xs">
            <motion.div
              animate={{
                textShadow: [
                  "0 0 20px rgba(0, 189, 126, 0.5)",
                  "0 0 40px rgba(0, 189, 126, 0.8)",
                  "0 0 20px rgba(0, 189, 126, 0.5)",
                ],
              }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="inline-block"
              style={{
                fontFamily: "Lexend, sans-serif",
                fontSize: "20px",
                fontWeight: 700,
                color: "#00BD7E",
              }}
            >
              MyBovine.ai
            </motion.div>
            <p
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              Leading the revolution in bovine intelligence and farm management
              with AI-powered solutions for modern agriculture.
            </p>
          </div>

          {/* Col 2 */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              About
            </span>
            <motion.a
              href="https://vanixtechnologies.com/about-us"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              About us
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/faqs"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              FAQs
            </motion.a>
          </div>

          {/* Col 3 */}
          <div className="flex flex-col gap-3">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Press
            </span>
            <motion.a
              href="https://vanixtechnologies.com/case-studies"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              Case studies
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/events"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              Events
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/blogs"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                display: "inline-block",
              }}
            >
              Blogs
            </motion.a>
          </div>

          {/* Col 4 */}
          <div className="flex flex-col gap-10">
            <div className="flex flex-col gap-3">
              <span
                className="uppercase"
                style={{
                  fontFamily: "Satoshi, sans-serif",
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#F5FFFA",
                  letterSpacing: "0.5px",
                }}
              >
                Let's connect
              </span>
              <div className="flex gap-4">
                <a
                  href="https://youtube.com/@vanixtechnologiesofficial?si=m84J-RyFofkFKa4N"
                  target="_blank"
                  style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="white"
                    strokeWidth="1"
                  >
                    <path d="M23 9.71a8.5 8.5 0 0 0-.91-4.13 2.92 2.92 0 0 0-1.72-1.12A69.28 69.28 0 0 0 12 4a69.28 69.28 0 0 0-8.37.46 2.92 2.92 0 0 0-1.72 1.12A8.5 8.5 0 0 0 1 9.71a57.61 57.61 0 0 0 0 4.58 8.5 8.5 0 0 0 .91 4.13 2.92 2.92 0 0 0 1.72 1.12A69.28 69.28 0 0 0 12 20a69.28 69.28 0 0 0 8.37-.46 2.92 2.92 0 0 0 1.72-1.12 8.5 8.5 0 0 0 .91-4.13 57.61 57.61 0 0 0 0-4.58zM9.74 14.85V9.15l5.15 2.85-5.15 2.85z" />
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/vanixtechnologies?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                  target="_blank"
                  style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="1.5"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="18" cy="6" r="1.5" fill="white" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/vanixtechnologies/"
                  target="_blank"
                  style={{ opacity: 0.7, transition: "opacity 0.3s" }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <span
                className="uppercase"
                style={{
                  fontFamily: "Satoshi, sans-serif",
                  fontWeight: 700,
                  fontSize: "14px",
                  color: "#F5FFFA",
                  letterSpacing: "0.5px",
                }}
              >
                Download mybovine.ai
              </span>
              <div className="flex gap-4">
                <motion.a
                  href="https://apps.apple.com/in/app/my-bovine-ai/id6758000909"
                  target="_blank"
                  whileHover={{ scale: 1.05 }}
                  style={{
                    display: "inline-flex",
                    height: "40px",
                    width: "120px",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/q3qd7nug_appstorebutton.svg"
                    alt="Download on App Store"
                    className="h-[36px] sm:h-[40px]"
                  />
                </motion.a>
                <motion.a
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.vanix.cattle.vanix_cattle&pcampaignid=web_share"
                  whileHover={{ scale: 1.05 }}
                  style={{
                    display: "inline-flex",
                    height: "40px",
                    width: "120px",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/zffwa1x8_googleplaystorebutton.svg"
                    alt="Get it on Google Play"
                    className="h-[36px] sm:h-[40px]"
                  />
                </motion.a>
              </div>
            </div>
          </div>

          {/* Col 5 */}
          <div className="flex flex-col gap-3 w-full md:w-[359px]">
            <span
              className="uppercase"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: "#F5FFFA",
                letterSpacing: "0.5px",
              }}
            >
              Subscribe to our Newsletters
            </span>
            <div className="flex flex-col gap-4 w-full">
              <form
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row gap-4"
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  disabled={isNewsletterLoading}
                  className="flex-grow h-10 px-4 py-3 rounded disabled:opacity-50"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    fontFamily: "Geist, sans-serif",
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#FEFEFF",
                    border: "none",
                    outline: "none",
                    flex: 1,
                  }}
                  data-testid="newsletter-input"
                />
                <motion.button
                  type="submit"
                  disabled={isNewsletterLoading}
                  whileHover={{ scale: isNewsletterLoading ? 1 : 1.05 }}
                  whileTap={{ scale: isNewsletterLoading ? 1 : 0.95 }}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    fontFamily: "Geist, sans-serif",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "#ffffff",
                    padding: "12px 24px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isNewsletterLoading ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                    opacity: isNewsletterLoading ? 0.6 : 1,
                  }}
                  data-testid="newsletter-submit"
                >
                  {isNewsletterLoading ? "Subscribing..." : "Subscribe"}
                </motion.button>
              </form>

              <p
                style={{
                  fontFamily: "Geist, sans-serif",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "rgba(255, 255, 255, 0.6)",
                  margin: 0,
                  lineHeight: "1.5",
                }}
              >
                Join the tech revolution and stay in the know. Subscribe for
                exclusive updates, offers, and a glimpse into the future
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div
          className="w-full flex flex-col md:flex-row justify-between items-center gap-6"
          data-testid="footer-bottom"
        >
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_cattle-intelligence/artifacts/4v1y8fl1_Vanixlogowhite.svg"
              alt="Vanix Logo"
              height="32"
              className="h-[32px]"
              data-testid="footer-logo"
            />

            <span
              className="text-center md:text-left"
              style={{
                fontFamily: "Satoshi, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "#ffffff",
              }}
            >
              © 2026 Vanix Technologies. All rights reserved.
            </span>
          </div>

          <div className="flex gap-6">
            <motion.a
              href="https://vanixtechnologies.com/privacy-policy"
              target="_blank"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                textDecoration: "none",
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.target.style.color = "rgba(255, 255, 255, 1)")
              }
              onMouseLeave={(e) =>
                (e.target.style.color = "rgba(255, 255, 255, 0.6)")
              }
              data-testid="privacy-link"
            >
              Privacy Policy
            </motion.a>
            <motion.a
              href="https://vanixtechnologies.com/terms-and-conditions"
              target="_blank"
              whileHover={{ scale: 1.05 }}
              style={{
                fontFamily: "Geist, sans-serif",
                fontWeight: 400,
                fontSize: "14px",
                color: "rgba(255, 255, 255, 0.6)",
                textDecoration: "none",
                transition: "color 0.3s",
              }}
              onMouseEnter={(e) =>
                (e.target.style.color = "rgba(255, 255, 255, 1)")
              }
              onMouseLeave={(e) =>
                (e.target.style.color = "rgba(255, 255, 255, 0.6)")
              }
              data-testid="terms-link"
            >
              Terms of Services
            </motion.a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============ LEAD CAPTURE MODAL ============
const LeadCaptureModal = ({ isOpen, onClose }) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    mobileNumber: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\D/g, "")))
      newErrors.mobileNumber = "Invalid mobile number";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        "https://plm-apis.vanixtechnologies.com/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const responseData = await response.json();
      const responseMessage =
        responseData?.message ||
        responseData?.description ||
        "Request processed successfully";

      if (response.ok) {
        showNotification(
          responseMessage ||
            "Farm assessment request submitted! Our team will reach out soon.",
          "success",
          3000,
        );

        setFormData({
          username: "",
          email: "",
          mobileNumber: "",
          address: "",
        });
        setErrors({});

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showNotification(
          responseMessage || "Something went wrong. Please try again.",
          "error",
          3000,
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showNotification(
        error.message ||
          "Unable to submit form. Please check your connection and try again.",
        "error",
        3000,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lead-capture-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl border border-black/10 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-capture-modal"
      >
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="modal-close-btn"
          type="button"
        >
          <X size={24} />
        </motion.button>

        <h2
          className="text-[24px] font-bold text-black"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Farm Assessment
        </h2>
        <p
          className="text-[14px] text-gray-600 mt-2"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Get a personalized assessment from our sales team
        </p>

        <form className="flex flex-col gap-4 mt-6" onSubmit={handleSubmit}>
          <div>
            <input
              type="text"
              name="username"
              placeholder="Your Full Name"
              value={formData.username}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.username
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-username"
              disabled={isLoading}
            />
            {errors.username && (
              <p className="text-red-500 text-[12px] mt-1">{errors.username}</p>
            )}
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.email
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-email"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-red-500 text-[12px] mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <input
              type="tel"
              name="mobileNumber"
              placeholder="Mobile Number (10 digits)"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors ${
                errors.mobileNumber
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-mobile"
              disabled={isLoading}
            />
            {errors.mobileNumber && (
              <p className="text-red-500 text-[12px] mt-1">
                {errors.mobileNumber}
              </p>
            )}
          </div>

          <div>
            <textarea
              name="address"
              placeholder="Farm Address / Location"
              value={formData.address}
              onChange={handleInputChange}
              rows="3"
              className={`w-full border rounded-xl px-4 py-3 text-[16px] outline-none transition-colors resize-none ${
                errors.address
                  ? "border-red-500 focus:border-red-500"
                  : "border-black/10 focus:border-[#00BD7E]"
              }`}
              style={{ fontFamily: "Satoshi, sans-serif" }}
              data-testid="modal-input-address"
              disabled={isLoading}
            />
            {errors.address && (
              <p className="text-red-500 text-[12px] mt-1">{errors.address}</p>
            )}
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            disabled={isLoading}
            className="bg-[#00BD7E] text-white text-[18px] font-medium rounded-xl px-6 py-3 w-full mt-2 hover:bg-[#00a86b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontFamily: "Satoshi, sans-serif" }}
            data-testid="modal-submit-btn"
          >
            {isLoading ? "Submitting..." : "Submit Assessment Request"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

// ============ BLOG COMPONENT ============
const BlogComponent = () => {
  // Image assets from Figma
  const imgHeroSection =
    "http://localhost:3845/assets/77f6281a08684478fa227979b4cef5721d6c7c95.png";
  const imgImage11 =
    "http://localhost:3845/assets/cd43dd9fd17ef227cd399d74e43fdb147a0b313a.png";
  const imgIcon =
    "http://localhost:3845/assets/57691f2003613e7a5eca72afae4deb5c856d2641.svg";

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <NotificationProvider>
      <div className="w-full min-h-screen flex flex-col bg-white" style={{ fontFamily: "Noto Sans, sans-serif" }}>
        {/* Navbar */}
        <DynamicNavbar onOpenModal={() => setIsModalOpen(true)} />

        {/* Lead Capture Modal */}
        <AnimatePresence>
          {isModalOpen && (
            <LeadCaptureModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="flex-1 pt-20 bg-white py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-12 lg:px-16 2xl:px-20">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12 md:mb-16 lg:mb-20"
            >
              <div className="relative isolate w-full rounded-[16px] overflow-hidden p-8 md:p-12 lg:p-16 min-h-[400px] md:min-h-[500px] flex flex-col justify-between">
                {/* Background Image */}
                <div className="absolute inset-0 -z-10">
                  <img
                    src={imgHeroSection}
                    alt="Hero Background"
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[rgba(20,27,44,0.8)] to-[rgba(66,90,146,0.48)]" />
                </div>

                {/* Hero Content */}
                <div className="relative z-10 flex flex-col gap-6 md:gap-8">
                  {/* Title */}
                  <h1
                    className="text-[42px] sm:text-[54px] md:text-[64px] lg:text-[72px] font-bold text-white leading-tight tracking-[-2px] md:tracking-[-3.6px]"
                    style={{
                      fontFamily: "Satoshi Variable, sans-serif",
                      maxWidth: "1000px",
                    }}
                  >
                    Signals Before Symptoms: Engineering a Deterministic Cattle
                    Monitoring System
                  </h1>

                  {/* Author & Date */}
                  <div className="flex flex-col gap-3">
                    <p
                      className="text-lg md:text-xl lg:text-2xl text-white font-medium"
                      style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                    >
                      By Rachita Reddy | Firmware Engineer | Vanix - MyBovine.ai
                    </p>
                    <p
                      className="text-lg md:text-xl lg:text-2xl text-white font-medium"
                      style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                    >
                      March 27, 2026
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
              {/* Main Content */}
              <motion.div
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2"
              >
                <div className="bg-[#f3f4f5] rounded-[12px] p-8 md:p-10 lg:p-12">
                  <div className="space-y-8 md:space-y-10">
                    {/* Intro Section */}
                    <div className="space-y-6">
                      <h2
                        className="text-2xl md:text-3xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        Why Farm Tech Usually Fails
                      </h2>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        If you've ever stepped into a professional dairy farm,
                        you know it's a place that hates electronics. Between
                        the thick concrete walls of the milking parlor, the high
                        humidity of the sheds, and the shifting mass of 500kg
                        animals that act as giant "signal absorbers," the farm
                        is a chaos-engine.
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        When I joined the Vanix and <strong>MyBovine.ai</strong>{" "}
                        project to oversee the firmware for our{" "}
                        <strong>BLE Gateway and Node systems</strong>, I
                        realized that a standard "IoT tracker schema" wouldn't
                        survive here. In this vertical, a missed diagnosis isn't
                        just a technical glitch, it's a "silent thief" that
                        steals a farmer's profit.
                      </p>
                    </div>

                    {/* Section 1 */}
                    <div className="space-y-6">
                      <h3
                        className="text-xl md:text-2xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        1. The Real-Time Foundation: Determinism vs. Best-Effort
                      </h3>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        The most critical decision in our journey was the choice
                        of Operating System. While many startups use basic
                        "best-effort" code,{" "}
                        <strong>
                          our early versions were built on Zephyr RTOS.
                        </strong>{" "}
                        In a standard consumer gadget, the processor might "lag"
                        while handling a background task. On a farm, that lag
                        can mean a missed fever spike. By using a{" "}
                        <strong>Real-Time Operating System (RTOS)</strong>, we
                        achieved <strong>Deterministic Execution</strong>.
                        Through <strong>preemptive multi-threading</strong>, our
                        firmware ensures that when a sensor detects a thermal
                        micro-drift or a movement vector indicating a silent
                        heat, the system prioritizes that alert instantly. It
                        doesn't wait for a cycle; it acts.
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        This architectural DNA is also how we hit our{" "}
                        <strong>2-year battery life</strong>. Zephyr allows for
                        granular <strong>Power Management</strong> putting the
                        radio and sensors into "Deep Sleep" for 99% of the time,
                        while the "Watchdog" timers stay alert to wake the
                        system the moment a health event is detected.
                      </p>
                    </div>

                    {/* Section 2 */}
                    <div className="space-y-6">
                      <h3
                        className="text-xl md:text-2xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        2. Collision Avoidance at Scale
                      </h3>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        Imagine a pasture with 100 cows all trying to "shout"
                        their health data to one Gateway at the exact same
                        millisecond. In a standard setup, those radio signals
                        crash — a phenomenon known as{" "}
                        <strong>Packet Collision.</strong>
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        To solve this, our engineering team developed a{" "}
                        <strong>
                          Collision-Resistant Broadcast Architecture
                        </strong>
                        . Instead of a random "free-for-all," we implemented a
                        proprietary timing logic that naturally de-synchronizes
                        the transmissions across the herd. It's an elegant,
                        low-power way to ensure our Gateway hears every single
                        "heartbeat" from the field. This clean data stream is
                        what powers the live monitoring at our{" "}
                        <strong>Hyderabad site.</strong>
                      </p>
                    </div>

                    {/* Image Section */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6 }}
                      className="rounded-[12px] overflow-hidden h-64 md:h-80 lg:h-96"
                    >
                      <img
                        src={imgImage11}
                        alt="Collision Avoidance Visualization"
                        className="w-full h-full object-cover"
                      />
                    </motion.div>

                    {/* Section 3 */}
                    <div className="space-y-6">
                      <h3
                        className="text-xl md:text-2xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        3. From Binary Payloads to Agentic AI
                      </h3>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        As a firmware engineer, I spend my day optimizing{" "}
                        <strong>Compact Binary Payloads</strong>. By stripping
                        away every unnecessary bit from the data packets, we
                        reduce <strong>Radio Airtime</strong>. Less time spent
                        transmitting means less battery drain and more "room" in
                        the airwaves for other nodes to talk.
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        But the real magic happens when this high-fidelity data
                        reaches the cloud. It feeds directly into our{" "}
                        <strong>Agentic AI</strong>. When you "talk" to{" "}
                        <strong>
                          your cattle in real time - real life like
                        </strong>
                        , you aren't getting a canned response. You are getting
                        a natural language summary derived from the exact{" "}
                        <strong>accelerometer vectors</strong> and{" "}
                        <strong>rumination patterns</strong> my firmware
                        captured just seconds ago.
                      </p>
                    </div>

                    {/* Section 4 */}
                    <div className="space-y-6">
                      <h3
                        className="text-xl md:text-2xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        4. Engineering for "Zero-Loss" Farming
                      </h3>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        Our hardware is <strong>IP67-rated</strong>, fully
                        waterproof and shock-resistant—because we know cows
                        aren't gentle with equipment. However, the real "armor"
                        is our <strong>Self-Healing Firmware</strong>.
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        If a cow wanders beyond the range of the Gateway, the
                        node doesn't just stop. It automatically begins{" "}
                        <strong>Data Buffering</strong>, storing critical health
                        events in local memory. The moment she steps back into
                        range, the firmware performs a high-speed "Handshake"
                        and re-syncs the missing data. This ensures a 100%
                        complete health history, with zero gaps in the timeline.
                      </p>
                    </div>

                    {/* Conclusion */}
                    <div className="space-y-6">
                      <h3
                        className="text-xl md:text-2xl font-bold text-[#191c1d]"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        Conclusion: The Data Before the Symptoms
                      </h3>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        At <strong>MyBovine.ai</strong>, we believe the most
                        valuable data is the data that arrives{" "}
                        <strong>before</strong> the symptoms do. We aren't just
                        tracking movement; we are engineering the foundation of
                        modern, proactive agriculture. I am proud to lead the
                        firmware initiatives that turn these "invisible signals"
                        into the security every dairy farmer deserves.
                      </p>

                      <p
                        className="text-base md:text-lg text-[#191c1d] leading-relaxed"
                        style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                      >
                        <strong>Ready to see the engineering in action?</strong>{" "}
                        If you're managing a professional herd and want to move
                        from reactive to proactive, reach out to our team at{" "}
                        <strong>sales@vanix.in</strong>. We'd love to walk you
                        through a farm assessment.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Sidebar */}
              <motion.div
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="lg:col-span-1"
              >
                <div
                  className="rounded-[16px] p-6 md:p-8 sticky top-20 lg:top-32"
                  style={{
                    backgroundImage:
                      "linear-gradient(128.54deg, #34cc80 2.04%, #0084d1 100.05%)",
                  }}
                >
                  <div className="space-y-6">
                    {/* Icon */}
                    <div className="h-8 md:h-10">
                      <img
                        src={imgIcon}
                        alt="Icon"
                        className="w-2 h-2 object-contain"
                      />
                    </div>

                    {/* Heading */}
                    <h3
                      className="text-lg md:text-xl font-bold text-white"
                      style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                    >
                      Ready to Modernise Your Farm?
                    </h3>

                    {/* Description */}
                    <p
                      className="text-sm md:text-base text-white/80"
                      style={{ fontFamily: "Satoshi Variable, sans-serif" }}
                    >
                      We are available 24/7 to assist with your herd management
                      deployment.
                    </p>

                    {/* CTA Button */}
                    <motion.button
                      onClick={() => setIsModalOpen(true)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full bg-white rounded-[12px] py-3 md:py-4 font-bold text-center transition-all cursor-pointer"
                      style={{
                        color: "#00BD7E",
                        fontFamily: "Satoshi Variable, sans-serif",
                        fontSize: "14px",
                      }}
                    >
                      Schedule a Call
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer onOpenModal={() => setIsModalOpen(true)} />
      </div>
    </NotificationProvider>
  );
};

export default BlogComponent;
